import { isAnnotationIdLocked } from './annotationLockUtils';
import buildNudgeAnnotationPayload from './buildNudgeAnnotationPayload';

export const MULTI_SELECTION_ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
  TOP: 'top',
  MIDDLE: 'middle',
  BOTTOM: 'bottom',
};

export const MULTI_SELECTION_DISTRIBUTE = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
};

export const ALIGN_TO_CANVAS = {
  CENTER_HORIZONTAL: 'canvas-center-horizontal',
  CENTER_VERTICAL: 'canvas-center-vertical',
};

export const MIN_ALIGN_SELECTION_COUNT = 2;
export const MIN_CANVAS_ALIGN_SELECTION_COUNT = 1;
export const MIN_DISTRIBUTE_SELECTION_COUNT = 3;

export const getSelectionGroupBounds = (clientRects) => {
  const minX = Math.min(...clientRects.map((rect) => rect.x));
  const maxX = Math.max(...clientRects.map((rect) => rect.x + rect.width));
  const minY = Math.min(...clientRects.map((rect) => rect.y));
  const maxY = Math.max(...clientRects.map((rect) => rect.y + rect.height));

  return {
    minX,
    maxX,
    centerX: (minX + maxX) / 2,
    minY,
    maxY,
    centerY: (minY + maxY) / 2,
  };
};

/** @deprecated use getSelectionGroupBounds */
export const getSelectionGroupHorizontalBounds = (clientRects) => {
  const { minX, maxX, centerX } = getSelectionGroupBounds(clientRects);
  return { minX, maxX, centerX };
};

export const computeAlignDelta = (rect, alignment, groupBounds) => {
  const { minX, maxX, centerX, minY, maxY, centerY } = groupBounds;

  switch (alignment) {
    case MULTI_SELECTION_ALIGN.LEFT:
      return { dx: minX - rect.x, dy: 0 };
    case MULTI_SELECTION_ALIGN.CENTER:
      return {
        dx: centerX - (rect.x + rect.width / 2),
        dy: 0,
      };
    case MULTI_SELECTION_ALIGN.RIGHT:
      return { dx: maxX - (rect.x + rect.width), dy: 0 };
    case MULTI_SELECTION_ALIGN.TOP:
      return { dx: 0, dy: minY - rect.y };
    case MULTI_SELECTION_ALIGN.MIDDLE:
      return {
        dx: 0,
        dy: centerY - (rect.y + rect.height / 2),
      };
    case MULTI_SELECTION_ALIGN.BOTTOM:
      return { dx: 0, dy: maxY - (rect.y + rect.height) };
    default:
      return { dx: 0, dy: 0 };
  }
};

/** @deprecated use computeAlignDelta */
export const computeHorizontalAlignDeltaX = (rect, alignment, groupBounds) =>
  computeAlignDelta(rect, alignment, groupBounds).dx;

/**
 * @param {Array<{ id: string, x: number, y: number, width: number, height: number }>} clientRects
 * @param {keyof MULTI_SELECTION_ALIGN} alignment
 * @param {Record<string, object>} annotations
 */
export const computeMultiSelectionAlignUpdates = (
  clientRects,
  alignment,
  annotations,
) => {
  if (!clientRects?.length || clientRects.length < MIN_ALIGN_SELECTION_COUNT) {
    return [];
  }

  const groupBounds = getSelectionGroupBounds(clientRects);

  return clientRects
    .map((rect) => {
      if (isAnnotationIdLocked(annotations, rect.id)) {
        return null;
      }
      const { dx, dy } = computeAlignDelta(rect, alignment, groupBounds);
      return buildNudgeAnnotationPayload(annotations[rect.id], dx, dy);
    })
    .filter(Boolean);
};

/**
 * Equal gaps between adjacent bounding boxes; first and last stay at group extremes.
 */
export const computeMultiSelectionDistributeUpdates = (
  clientRects,
  direction,
  annotations,
) => {
  if (
    !clientRects?.length ||
    clientRects.length < MIN_DISTRIBUTE_SELECTION_COUNT
  ) {
    return [];
  }

  const horizontal = direction === MULTI_SELECTION_DISTRIBUTE.HORIZONTAL;
  const sorted = [...clientRects].sort((a, b) =>
    horizontal ? a.x - b.x : a.y - b.y,
  );
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  if (horizontal) {
    const totalSpan = last.x + last.width - first.x;
    const sumWidths = sorted.reduce((sum, rect) => sum + rect.width, 0);
    const gap = (totalSpan - sumWidths) / (sorted.length - 1);
    let cursorX = first.x;

    return sorted
      .map((rect) => {
        if (isAnnotationIdLocked(annotations, rect.id)) {
          return null;
        }
        const deltaX = cursorX - rect.x;
        const update = buildNudgeAnnotationPayload(
          annotations[rect.id],
          deltaX,
          0,
        );
        cursorX += rect.width + gap;
        return update;
      })
      .filter(Boolean);
  }

  const totalSpan = last.y + last.height - first.y;
  const sumHeights = sorted.reduce((sum, rect) => sum + rect.height, 0);
  const gap = (totalSpan - sumHeights) / (sorted.length - 1);
  let cursorY = first.y;

  return sorted
    .map((rect) => {
      if (isAnnotationIdLocked(annotations, rect.id)) {
        return null;
      }
      const deltaY = cursorY - rect.y;
      const update = buildNudgeAnnotationPayload(
        annotations[rect.id],
        0,
        deltaY,
      );
      cursorY += rect.height + gap;
      return update;
    })
    .filter(Boolean);
};

export const getCanvasBoundsFromDesignLayer = (designLayer) => {
  const {
    clipWidth = 0,
    clipHeight = 0,
    clipX = 0,
    clipY = 0,
  } = designLayer?.attrs ?? {};

  return {
    minX: clipX,
    minY: clipY,
    maxX: clipX + clipWidth,
    maxY: clipY + clipHeight,
    centerX: clipX + clipWidth / 2,
    centerY: clipY + clipHeight / 2,
  };
};

/**
 * Move the whole selection so its group bounds align to the canvas clip area.
 */
export const computeSelectionAlignToCanvasUpdates = (
  clientRects,
  alignment,
  annotations,
  designLayer,
) => {
  if (
    !clientRects?.length ||
    clientRects.length < MIN_CANVAS_ALIGN_SELECTION_COUNT ||
    !designLayer
  ) {
    return [];
  }

  const groupBounds = getSelectionGroupBounds(clientRects);
  const canvasBounds = getCanvasBoundsFromDesignLayer(designLayer);
  let dx = 0;
  let dy = 0;

  if (alignment === ALIGN_TO_CANVAS.CENTER_HORIZONTAL) {
    dx = canvasBounds.centerX - groupBounds.centerX;
  } else if (alignment === ALIGN_TO_CANVAS.CENTER_VERTICAL) {
    dy = canvasBounds.centerY - groupBounds.centerY;
  } else {
    return [];
  }

  if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
    return [];
  }

  return clientRects
    .map((rect) => {
      if (isAnnotationIdLocked(annotations, rect.id)) {
        return null;
      }
      return buildNudgeAnnotationPayload(annotations[rect.id], dx, dy);
    })
    .filter(Boolean);
};

/** @deprecated use computeMultiSelectionAlignUpdates */
const computeMultiSelectionHorizontalAlignUpdates = (
  clientRects,
  alignment,
  annotations,
) => computeMultiSelectionAlignUpdates(clientRects, alignment, annotations);

export default computeMultiSelectionHorizontalAlignUpdates;
